import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Heart, DollarSign, Users, Package, HandHeart } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Support = () => {
  const { toast } = useToast();
  const [donationAmount, setDonationAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  const suggestedAmounts = [25, 50, 100, 250, 500, 1000];

  const handleDonation = async (amount) => {
    try {
      await axios.post(`${API}/donations`, { amount, donorEmail: null, donorName: null });
      toast({
        title: "Thank You!",
        description: `Your donation of $${amount} has been processed successfully.`,
      });
    } catch (error) {
      console.error('Error processing donation:', error);
      toast({
        title: "Processing Failed",
        description: "There was an error processing your donation. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/support-inquiry`, {
        ...contactForm,
        inquiryType: 'grant_partnership'
      });
      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. We'll get back to you soon!",
      });
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error sending your message. Please try again.",
        variant: "destructive"
      });
    }
  };

  const supportWays = [
    {
      icon: DollarSign,
      title: 'Financial Donations',
      description: 'Your monetary contributions help fund our programs, events, and operational costs.'
    },
    {
      icon: HandHeart,
      title: 'Volunteer',
      description: 'Offer your time and skills to help at events, workshops, and community service projects.'
    },
    {
      icon: Package,
      title: 'In-Kind Donations',
      description: 'Donate supplies, materials, or services that support our cultural and educational programs.'
    },
    {
      icon: Users,
      title: 'Sponsor an Event',
      description: 'Support specific events or programs through event sponsorships.'
    }
  ];

  const impactAreas = [
    { amount: '$25', impact: 'Provides cultural arts supplies for one young woman for a quarter' },
    { amount: '$50', impact: 'Covers transportation costs for one member to attend a regional event' },
    { amount: '$100', impact: 'Sponsors a cultural workshop or Aun"tea" Time gathering' },
    { amount: '$250', impact: 'Funds quarterly meeting supplies and refreshments' },
    { amount: '$500', impact: 'Supports travel costs for members to attend national conferences' },
    { amount: '$1,000', impact: 'Fully funds a major project like Rights of Nature or Softball for Sovereignty for a quarter' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 md:py-24" style={{ backgroundColor: '#e37c55' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Support Naiwa Daughters
            </h1>
            <p className="text-lg md:text-xl leading-relaxed opacity-95">
              Your support empowers young Indigenous women to become the leaders their communities need. Every contribution makes a real difference.
            </p>
          </div>
        </div>
      </section>

      {/* Ways to Support */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#7e2f22' }}>Ways to Support</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {supportWays.map((way, index) => {
                const IconComponent = way.icon;
                return (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="pt-8 pb-8 text-center">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e37c5520' }}>
                        <IconComponent className="h-8 w-8" style={{ color: '#e37c55' }} />
                      </div>
                      <h3 className="text-lg font-bold mb-2" style={{ color: '#7e2f22' }}>{way.title}</h3>
                      <p className="text-sm text-gray-600">{way.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Donation Impact */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4" style={{ color: '#7e2f22' }}>Your Impact</h2>
              <p className="text-lg text-gray-600">See how your donation directly supports our programs and young women leaders</p>
            </div>
            <Card className="border-0 shadow-xl">
              <CardContent className="pt-8 pb-8">
                <div className="space-y-6">
                  {impactAreas.map((area, index) => (
                    <div key={index} className="flex items-start space-x-4 p-4 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center font-bold text-white" style={{ backgroundColor: '#53a5ca' }}>
                          {area.amount}
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-700">{area.impact}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-3xl" style={{ color: '#7e2f22' }}>Make a Donation</CardTitle>
                <CardDescription>Choose an amount or enter your own. Every dollar helps empower the next generation of Indigenous women leaders.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Suggested Amounts */}
                  <div>
                    <Label className="mb-3 block text-base">Select Amount</Label>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                      {suggestedAmounts.map((amount) => (
                        <Button
                          key={amount}
                          variant="outline"
                          className="h-16 text-lg font-semibold border-2 hover:text-white transition-all duration-200"
                          style={donationAmount === amount ? { 
                            backgroundColor: '#53a5ca', 
                            borderColor: '#53a5ca',
                            color: 'white'
                          } : {
                            borderColor: '#53a5ca20',
                            color: '#53a5ca'
                          }}
                          onClick={() => setDonationAmount(amount)}
                        >
                          ${amount}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Amount */}
                  <div className="space-y-2">
                    <Label htmlFor="customAmount" className="text-base">Or Enter Custom Amount</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">$</span>
                      <Input
                        id="customAmount"
                        type="number"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        className="pl-8 text-lg h-12"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {/* Donation Button */}
                  <Button
                    size="lg"
                    className="w-full text-white hover:opacity-90 transition-all duration-200 text-lg h-14"
                    style={{ backgroundColor: '#e37c55' }}
                    onClick={() => handleDonation(donationAmount || customAmount)}
                    disabled={!donationAmount && !customAmount}
                  >
                    <Heart className="mr-2 h-6 w-6" />
                    Donate {(donationAmount || customAmount) && `$${donationAmount || customAmount}`}
                  </Button>

                  <p className="text-sm text-center text-gray-600 mt-4">
                    Naiwa Daughters is a 501(c)(3) nonprofit organization. Your donation is tax-deductible to the extent allowed by law.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Grant & Partnership Inquiry */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl" style={{ color: '#7e2f22' }}>Grant & Partnership Opportunities</CardTitle>
                <CardDescription>Interested in supporting us through a grant or partnership? Let's talk!</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input 
                        id="name" 
                        name="name" 
                        value={contactForm.name} 
                        onChange={handleContactChange} 
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email" 
                        name="email" 
                        type="email" 
                        value={contactForm.email} 
                        onChange={handleContactChange} 
                        required 
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea 
                      id="message" 
                      name="message" 
                      value={contactForm.message} 
                      onChange={handleContactChange} 
                      rows={4}
                      placeholder="Tell us about your organization and how you'd like to support Naiwa Daughters..."
                      required 
                    />
                  </div>
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full text-white hover:opacity-90 transition-all duration-200"
                    style={{ backgroundColor: '#53a5ca' }}
                  >
                    Send Inquiry
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Thank You Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Heart className="h-16 w-16 mx-auto mb-6" style={{ color: '#e37c55' }} />
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#7e2f22' }}>Thank You</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Your support makes it possible for us to empower young Indigenous women, preserve Cherokee culture, and build the next generation of leaders. Every contribution, no matter the size, creates lasting impact in our community.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support;
